import React from 'react';
import { useBreakpoints } from 'hooks/useBreakpoints';

// Helper function to generate dynamic ranges
function generateDynamicRanges(breaks) {
  if (!breaks || breaks.length < 2) return [];
  
  // Check if all breaks are integers
  const allIntegers = breaks.every(b => Number.isInteger(b));
  
  const ranges = [];
  for (let i = 0; i < breaks.length - 1; i++) {
    const start = breaks[i];
    const next = breaks[i + 1];
    const diff = next - start;
    let end;
    
    if (allIntegers) {
      // For integers — subtract 5% of step or at least 1
      const decrement = Math.max(Math.ceil(diff * 0.05), 1);
      end = next - decrement;
      if (end <= start) end = next - 1; // fallback safety
    } else {
      // For floats — shift about 1/3 toward the next break
      const decrement = diff * 0.33;
      end = +(start + diff - decrement).toFixed(2);
    }
    
    ranges.push(`${start} to ${end}`);
  }

  return ranges;
}

const MarkerLegend = ({
  showIndicator3,
  showIndicator4,
  showIndicator5,
  indicator3Breaks = [],
  indicator4Breaks = [],
  indicator5Breaks = [],
  colors,
  setMapConfigData
}) => {
        const { breakpoints } = useBreakpoints();
        //=========== Mobile View =============
        const isMobile = breakpoints.down('md');
  const indicator_3_name = setMapConfigData?.indicators[0]?.label;
  const indicator_4_name = setMapConfigData?.indicators[1]?.label;
  const indicator_5_name = setMapConfigData?.indicators[2]?.label;
  const indicator_3_sub_name = setMapConfigData?.indicators[0]?.subIndicator?.label;
  const indicator_4_sub_name = setMapConfigData?.indicators[1]?.subIndicator?.label;
  const indicator_5_sub_name = setMapConfigData?.indicators[2]?.subIndicator?.label;

  const hasIndicator = showIndicator3 || showIndicator4 || showIndicator5;
  const hasLabel = indicator_3_name || indicator_4_name || indicator_5_name;
  if (!hasIndicator || !hasLabel) return null;

  const getShapeStyle = (color, shape) => {
    const baseStyle = {
      width: '20px',
      height: '20px',
      backgroundColor: color,
      marginBottom: '0.25rem',
      flexShrink: 0
    };

    switch (shape) {
      case 'triangle':
        return {
          width: '0',
          height: '0',
          borderLeft: '10px solid transparent',
          borderRight: '10px solid transparent',
          borderBottom: `20px solid ${color}`,
          marginBottom: '0.25rem'
        };
      case 'square':
        return baseStyle;
      case 'hexagon':
        return {
          width: '24px',
          height: '20px',
          backgroundColor: color,
          clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
          marginBottom: '0.25rem'
        };
      default:
        return baseStyle;
    }
  };

  const renderLegendItems = (breaks, shapeType, prefix) => {
    const reversedColors = [...colors].reverse();

    // Generate dynamic range labels
    const rangeLabels = generateDynamicRanges(breaks);
    
    // Add the final range: from second-to-last to last break
    if (breaks.length >= 2) {
      const secondLast = breaks[breaks.length - 2];
      const last = breaks[breaks.length - 1];
      rangeLabels.push(`${secondLast} to ${last}`);
    }

    return (
      <div className="d-flex flex-column align-items-center gap-3">
        {breaks.map((val, i) => {
          const nextBreak = i < breaks.length - 1 ? breaks[i + 1] : null;
          const color = reversedColors[Math.min(i + 1, reversedColors.length - 1)];
          const label = rangeLabels[i] || `${val.toLocaleString()}+`;

          return (
            <div key={`${prefix}-${i}`} className="d-flex flex-column align-items-center">
              {nextBreak && (
                <>
                  <div style={getShapeStyle(color, shapeType)}></div>
                  <small className="text-center mt-1" style={{ fontSize: '0.75rem' }}>
                    {label}
                  </small>
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Validation function for numeric breaks
  const isValidBreaks = (arr) =>
    Array.isArray(arr) && arr.every(b => !isNaN(parseFloat(b)));

  return (
    <>
      {showIndicator3 && indicator_3_name && (
        <div
          className="legend-box bg-white border rounded shadow p-3 mt-2 gap-4"
          style={{ flex: '1 1 auto',  width: isMobile ? "calc(100% - 1rem)" : "calc(20%)", margin: '0.5rem' }}
        >
          {isValidBreaks(indicator3Breaks) && indicator3Breaks.length > 1 ? (
            <div className="mt-3 mb-2">
              {renderLegendItems(indicator3Breaks, 'triangle', 'tri')}
            </div>
          ) : (
            <div className="mt-3 mb-2">
              Data not available
            </div>
          )}
          <div className="fw-bold">
            {indicator_3_name}
            {indicator_3_sub_name && (
              <span className="text-success"> | [{indicator_3_sub_name}]</span>
            )}
          </div>
        </div>
      )}

      {showIndicator4 && indicator_4_name && (
        <div
          className="legend-box bg-white border rounded shadow p-3 mt-2 gap-4"
          style={{ flex: '1 1 auto',  width: isMobile ? "calc(100% - 1rem)" : "calc(20%)", margin: '0.5rem' }}
        >
          {isValidBreaks(indicator4Breaks) && indicator4Breaks.length > 1 ? (
            <div className="mt-3 mb-2">
              {renderLegendItems(indicator4Breaks, 'square', 'square')}
            </div>
          ) : (
            <div className="mt-3 mb-2">
              Data not available
            </div>
          )}
          <div className="fw-bold">
            {indicator_4_name}
            {indicator_4_sub_name && (
              <span className="text-success"> | [{indicator_4_sub_name}]</span>
            )}
          </div>
        </div>
      )}

      {showIndicator5 && indicator_5_name && (
        <div
          className="legend-box bg-white border rounded shadow p-3 mt-2 gap-4"
          style={{ flex: '1 1 auto', width: isMobile ? "calc(100% - 1rem)" : "calc(20%)", margin: '0.5rem' }}
        >
          {isValidBreaks(indicator5Breaks) && indicator5Breaks.length > 1 ? (
            <div className="mt-3 mb-2">
              {renderLegendItems(indicator5Breaks, 'hexagon', 'hex')}
            </div>
          ) : (
            <div className="mt-3 mb-2">
              Data not available
            </div>
          )}
          <div className="fw-bold">
            {indicator_5_name}
            {indicator_5_sub_name && (
              <span className="text-success"> | [{indicator_5_sub_name}]</span>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MarkerLegend;